// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React from 'react';
import { CloseIcon } from './CloseIcon';
import './TagChip.css';

/**
 * Fixed 12-swatch palette token names (mirrors
 * {@code com.loa.service.tag.TagPalette.TOKENS} on the resource server —
 * see docs/specs/tags-prd.md FR-3). Kept as a union so callers get
 * autocomplete/typo-safety; an unrecognised token still renders (falls back
 * to the "slate" swatch via CSS) rather than throwing, since a fund's
 * `tagIds` may resolve against a tag whose colour token changed underneath.
 */
export type TagColorToken =
  | 'slate'
  | 'red'
  | 'orange'
  | 'amber'
  | 'emerald'
  | 'teal'
  | 'sky'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'fuchsia'
  | 'rose';

export interface TagChipProps {
  /** Display name (original casing). */
  name: string;
  /** Palette colour token — see {@link TagColorToken}. */
  color: string;
  /** Archived tags render greyed regardless of their colour token. */
  archived?: boolean;
  /** Renders a small "x" affordance; omit to render a read-only chip. */
  onRemove?: () => void;
  /** Optional click handler on the chip body (e.g. open the tag filter). */
  onClick?: () => void;
  className?: string;
  'data-testid'?: string;
}

const MAX_CHARS = 18;

/**
 * A single tag pill. Truncates the display name at ~18 chars with the full
 * name available via the native `title` tooltip (PRD §9). Never colour-only
 * — the name is always rendered as text.
 */
export function TagChip({
  name,
  color,
  archived = false,
  onRemove,
  onClick,
  className = '',
  'data-testid': dataTestId,
}: TagChipProps) {
  const truncated = name.length > MAX_CHARS ? `${name.slice(0, MAX_CHARS - 1)}…` : name;
  const isTruncated = truncated !== name;

  const classes = [
    'fin-tag-chip',
    `fin-tag-chip--${color}`,
    archived && 'fin-tag-chip--archived',
    onClick && 'fin-tag-chip--clickable',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // A <button> cannot contain another <button> (invalid HTML / hydration
  // error) — the remove affordance is itself a <button>, so the outer
  // element can only be a real <button> when there's no remove affordance.
  // With both onClick + onRemove present, fall back to a span with
  // role="button" + keyboard handling so the chip stays clickable and
  // accessible without nesting interactive elements.
  const isPlainButton = !!onClick && !onRemove;
  const isSpanButton = !!onClick && !!onRemove;
  const Tag = isPlainButton ? 'button' : 'span';

  const handleKeyDown = isSpanButton
    ? (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }
    : undefined;

  return (
    <Tag
      type={isPlainButton ? 'button' : undefined}
      className={classes}
      title={isTruncated ? name : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={isSpanButton ? 'button' : undefined}
      tabIndex={isSpanButton ? 0 : undefined}
      data-testid={dataTestId}
    >
      <span className="fin-tag-chip__label">{truncated}</span>
      {archived && <span className="fin-tag-chip__archived-label">(archived)</span>}
      {onRemove && (
        <button
          type="button"
          className="fin-tag-chip__remove"
          aria-label={`Remove ${name} tag`}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <CloseIcon size={10} color="currentColor" aria-hidden />
        </button>
      )}
    </Tag>
  );
}
